import React from "react";
import { Typography } from "@material-ui/core";
import {
  AddIcon,
  CloseIcon,
  AisleOneSidedIcon,
  AisleTwoSidedIcon,
} from "../../../components/common/iconography";

export const AttachmentsHelp = ({ classes }) => (
  <>
    <Typography variant="subtitle1">
      <strong>Attachments</strong>
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Document Categories:</strong> Add unique document categories
          for categorizing assetts within Document Repository. Use of categories
          is optional.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Logo:</strong> Attach corporate logo image to be used for
          branding your assetts. We recommend using a transparent .png file
          format.{" "}
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Yard Map:</strong> Attach image of graphical layout of the
          Reload Yard.
        </Typography>
      </li>
    </ul>
  </>
);

export const BusinessInformationHelp = ({ classes }) => (
  <>
    <Typography variant="subtitle1">
      <strong>Business Information</strong>
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Name:</strong> Full business name.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Phone:</strong> Primary business phone number.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Email:</strong> Billing email address.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Website:</strong> Business website URL, if available.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Address:</strong> Physical Reload Yard address.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Invoice Number Prefix:</strong> Used for generating unique
          invoice numbers (E.g. use company initials){" "}
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Accounting Company ID:</strong> Shown, and required, once a
          Zapier invoice sync is active. The ID of the company file in your
          accounting system that your Zaps send invoices to. QuickBooks Online:
          the Company ID (realmId), under Settings &gt; Additional info, or
          Settings &gt; Subscriptions and billing. NetSuite: the Account ID,
          under Setup &gt; Company &gt; Company Information (sandboxes end in
          _SB1). Sage Intacct: the Company ID you sign in with. Zoho Books: the
          Organization ID, under Settings &gt; Organization Profile. Xero and
          Business Central: the tenant or company ID (a GUID) your Zapier
          connection uses.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Tax Rates:</strong> Map the Tax Codes and Tax Rates defined in
          QuickBooks. Must have at least 1 rate defined. If Tax Code or Tax Rate
          is changed later, you will need to visit each Account and select the
          new Tax Rate Code for all services where it is used.
        </Typography>
      </li>
    </ul>
  </>
);

export const CommoditiesHelp = ({ classes }) => (
  <>
    <Typography variant="subtitle1">
      <strong>Commodities</strong>
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Commodities:</strong> Select Commodities handled. Define
          default Units of Measure for each commodity.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Equipment / Capabilities:</strong> Select pieces of equipment
          in operation.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Forklift:</strong> Indicate Maximum weight capacity.
        </Typography>
      </li>
    </ul>
  </>
);

export const AccessAndCapacityHelp = ({ classes }) => (
  <>
    <Typography variant="subtitle1">
      <strong>Access and Capacity</strong>
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Open Air Yard Capacity:</strong> Outdoors yard area / capacity
          in Acres.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Covered / Warehouse Capacity:</strong> Indoors warehouse
          capacity in Square Feet.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Track Capcity:</strong> Yard track length in Feet.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Serving Rail Carrier:</strong> Primary rail service.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Accessible Highway(s):</strong> List nearby intestates and
          major highways. Enter one at a time and press the{" "}
          <AddIcon className={classes.icon} />
          <strong>ADD</strong> button to add to a list.
        </Typography>
      </li>
    </ul>
  </>
);

export const LocationsHelp = ({ classes }) => (
  <>
    <Typography variant="subtitle1">
      <strong>Locations</strong>
    </Typography>
    <Typography variant="subtitle1">
      Provide all named Yard Locations in a sequential/walking order, beginning
      from first aisle, moving from one location to the next. Include both
      outdoor yard and warehouse locations, if any. These will be used to guide
      crews through each location to perform Inventory Audits. The Location
      Layout wizard can handle one-sided, double-sided aisles, end-caps, and
      custom location names. location full name consists of: Area Prefix + Zone
      Prefix + Location Name.
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Areas:</strong> Add / Edit / Delete areas in your transload.
          Examples include Yard / Lot, Warehouse, Tracks.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Zones:</strong> Add / Edit / Delete designated zones for each
          Area. You can drag and drop zones to re-order Zone dropdown options.
          Clicking Zone name opens Zone Edit dialog. Zone examples include
          Hazmat / Covered, Climate Control, Customer-assigned.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Locations:</strong> Physical yard locations (aisles, pads,
          enclosed pans, tracks, etc.).
        </Typography>
      </li>
      <ul>
        <li>
          <Typography>
            <strong>Add Location:</strong> Enter one location at a time and
            press the <AddIcon className={classes.icon} />
            <strong>ADD</strong> button or hit Enter key.
          </Typography>
        </li>
        <li>
          <Typography>
            <strong>Area:</strong> Select one of the pre-defined areas where to
            add new Location.
          </Typography>
        </li>
        <li>
          <Typography>
            <strong>Zone:</strong> Select one of the pre-defined zones where to
            add new Location.
          </Typography>
        </li>
        <li>
          <Typography>
            <strong>Aisle Layout:</strong> Select{" "}
            <AisleOneSidedIcon className={classes.icon} />{" "}
            one-sided/end-cap/custom or{" "}
            <AisleTwoSidedIcon className={classes.icon} /> double-sided aisle
            layout (sides A &amp; B) using the toggle buttons.
          </Typography>
        </li>
        <li>
          <Typography>
            <AddIcon className={classes.icon} />
            <strong>ADD: </strong>
            Press the ADD button, or hit Enter key. Adding a two-sided aisle
            location, will add two locations (A and B).
          </Typography>
        </li>
        <li>
          <Typography>
            <strong>Location List</strong> You can remove unnecessary/mistakenly
            added location by pressing <CloseIcon className={classes.icon} />{" "}
            icon next to location name. You can also re-arrange locations by
            dragging and dropping into correct order. Clicking on location name,
            opens Location Edit dialog.
          </Typography>
        </li>
      </ul>
    </ul>
  </>
);
